import React, { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import accountsApi from "../../services/accountsCommonService";
import { formatINR, todayInput } from "./accountUser";
import { Field, controlClass } from "./FormFields";
import PageShell from "./PageShell";
import useAccountMeta from "./useAccountMeta";
import "./accounts.css";

export function ChequeRegister() {
  const { meta, labelOf } = useAccountMeta();
  const [rows, setRows] = useState([]);
  const [direction, setDirection] = useState("");
  const [status, setStatus] = useState("");
  const [postDated, setPostDated] = useState("");
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ newStatus: "", date: todayInput(), remarks: "", bounceReason: "", bounceCharges: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  const load = async (event) => {
    if (event) event.preventDefault();
    setError("");
    try {
      const result = await accountsApi.cheques({ direction, status, postDated, per_page: 100 });
      setRows(result?.data || []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (row, newStatus) => {
    setActive(row);
    setForm({ newStatus, date: todayInput(), remarks: "", bounceReason: "", bounceCharges: "" });
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const nextErrors = {};
    if (!form.date) nextErrors.date = "Date is required";
    if (form.newStatus === "BOUNCED" && !String(form.bounceReason || "").trim()) {
      nextErrors.bounceReason = "Bounce reason is required";
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    try {
      await accountsApi.chequeStatus(active._id, {
        ...form,
        bounceCharges: form.bounceCharges ? Number(form.bounceCharges) : undefined,
      });
      setActive(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <PageShell title="Cheque register" subtitle="Deposit, clear, bounce, or cancel">
      {error && <div className="accounts-error">{error}</div>}
      <form className="accounts-form" onSubmit={load}>
        <Row className="align-items-end">
          <Col xs={12} md={3} className="mb-3">
            <label className="form-label">Direction</label>
            <select className="form-select" value={direction} onChange={(event) => setDirection(event.target.value)}>
              <option value="">Both</option>
              {(meta?.chequeDirections || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </Col>
          <Col xs={12} md={3} className="mb-3">
            <label className="form-label">Status</label>
            <select className="form-select" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">Any status</option>
              {(meta?.chequeStatuses || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </Col>
          <Col xs={12} md={3} className="mb-3">
            <label className="form-label">Date</label>
            <select className="form-select" value={postDated} onChange={(event) => setPostDated(event.target.value)}>
              <option value="">All dates</option>
              {(meta?.postDatedFilters || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </Col>
          <Col xs={12} md="auto" className="mb-3">
            <div className="accounts-toolbar">
              <button className="btn btn-outline-secondary" type="submit">Filter</button>
            </div>
          </Col>
        </Row>
      </form>
      <div className="accounts-table-wrap">
      <table className="accounts-table">
        <thead>
          <tr><th>No</th><th>Date</th><th>Direction</th><th>Party</th><th className="num">Amount</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id}>
              <td>{row.chequeNo}{row.postDated ? " PDC" : ""}</td>
              <td>{row.chequeDate}</td>
              <td>{labelOf("chequeDirections", row.direction)}</td>
              <td>{row.partyName}</td>
              <td className="num">{formatINR(row.amount)}</td>
              <td>{labelOf("chequeStatuses", row.status)}</td>
              <td className="accounts-actions">
                {((meta?.chequeNext || {})[row.status] || []).map((next) => (
                  <button key={next} type="button" className="btn btn-outline-primary btn-sm" onClick={() => open(row, next)}>{labelOf("chequeStatuses", next)}</button>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {active && (
        <form className="accounts-form" onSubmit={submit} noValidate>
          <h3>{form.newStatus} cheque {active.chequeNo}</h3>
          <Row>
            <Field label="Date" required error={fieldErrors.date}>
              <input className={controlClass(fieldErrors.date)} type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
            </Field>
            <Field label="Remarks">
              <input className="form-control" value={form.remarks} onChange={(event) => setForm({ ...form, remarks: event.target.value })} />
            </Field>
            {form.newStatus === "BOUNCED" && (
              <>
                <Field label="Bounce reason" required error={fieldErrors.bounceReason}>
                  <input className={controlClass(fieldErrors.bounceReason)} value={form.bounceReason} onChange={(event) => setForm({ ...form, bounceReason: event.target.value })} />
                </Field>
                <Field label="Bounce charges">
                  <input className="form-control" type="number" min="0" step="0.01" value={form.bounceCharges} onChange={(event) => setForm({ ...form, bounceCharges: event.target.value })} />
                </Field>
              </>
            )}
          </Row>
          <div className="accounts-form-actions">
            <button className="btn btn-outline-secondary" type="button" onClick={() => setActive(null)}>Close</button>
            <button className="btn btn-primary" type="submit">Update</button>
          </div>
        </form>
      )}
    </PageShell>
  );
}

export function PortalPage() {
  const [invoices, setInvoices] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([accountsApi.myInvoices(), accountsApi.myReceipts(), accountsApi.myStatement()])
      .then(([invoiceResult, receiptResult, statementResult]) => {
        setInvoices(invoiceResult.data);
        setReceipts(receiptResult.data || []);
        setStatement(statementResult.data);
      })
      .catch((err) => setError(err.message));
  }, []);

  const download = async (id) => {
    try {
      const blob = await accountsApi.receiptPdf(id);
      window.open(URL.createObjectURL(blob));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <PageShell title="My account" subtitle="Invoices, receipts, and statement">
      {error && <div className="accounts-error">{error}</div>}
      {invoices && <p>{invoices.partyName} outstanding {formatINR(invoices.outstanding)}</p>}
      <h3>Invoices</h3>
      <div className="accounts-table-wrap">
      <table className="accounts-table">
        <thead><tr><th>No</th><th>Date</th><th>Due</th><th className="num">Amount</th><th className="num">Outstanding</th><th>Status</th></tr></thead>
        <tbody>
          {(invoices?.invoices || []).map((row) => (
            <tr key={row.voucherId}>
              <td>{row.voucherNo}</td>
              <td>{row.voucherDate}</td>
              <td>{row.dueDate}</td>
              <td className="num">{formatINR(row.amount)}</td>
              <td className="num">{formatINR(row.outstanding)}</td>
              <td>{row.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <h3>Receipts</h3>
      <div className="accounts-table-wrap">
      <table className="accounts-table">
        <thead><tr><th>No</th><th>Date</th><th className="num">Amount</th><th></th></tr></thead>
        <tbody>
          {receipts.map((row) => (
            <tr key={row._id}>
              <td>{row.voucherNo}</td>
              <td>{row.voucherDate}</td>
              <td className="num">{formatINR(row.amount)}</td>
              <td><button type="button" className="accounts-icon-btn" title="Download" aria-label="Download" onClick={() => download(row._id)}><i className="bi bi-download" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {statement && (
        <>
          <h3>Statement Â· closing {formatINR(statement.closingBalance?.amount)} {statement.closingBalance?.type}</h3>
          <div className="accounts-table-wrap">
          <table className="accounts-table">
            <thead><tr><th>Date</th><th>Voucher</th><th>Particulars</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr></thead>
            <tbody>
              {(statement.rows || []).map((row) => (
                <tr key={row.voucherId + row.date}>
                  <td>{row.date}</td>
                  <td>{row.voucherNo}</td>
                  <td>{row.particulars}</td>
                  <td className="num">{formatINR(row.debit)}</td>
                  <td className="num">{formatINR(row.credit)}</td>
                  <td className="num">{formatINR(row.balance?.amount ?? row.balance)} {row.balanceType || row.balance?.type || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </PageShell>
  );
}
